import axios from 'axios';
import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';

import { API_BASE_URL } from '../../config';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showAIModal, setShowAIModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    excerpt: '',
    category: '',
    tags: [],
    content_type: 'article',
    template_key: 'editorial_article',
    visibility: 'public',
    audience: [],
    sector: [],
    region: [],
    topics: [],
    author_role: '',
    editor_id: '',
    reviewer_id: '',
    scheduled_at: '',
    featured_rank: '',
    content_blocks: '',
    media_type: 'image',
    media_url: '',
    video_url: '',
    video_duration_seconds: '',
    video_caption: '',
    external_url: '',
    asset_refs: '',
    hero_headline: '',
    hero_supporting_line: '',
    primary_cta_label: '',
    primary_cta_url: '',
    secondary_cta_label: '',
    secondary_cta_url: '',
    featured_image: '',
    featured_image_alt: '',
    meta_title: '',
    meta_description: '',
    is_featured: false,
    status: 'draft'
  });

  // AI generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiOptions, setAiOptions] = useState({
    category: 'AI & Technology',
    tone: 'professional',
    length: 'medium',
    include_images: true,
    imageStyle: 'photorealistic'
  });

  const [tagInput, setTagInput] = useState('');
  const [audienceInput, setAudienceInput] = useState('');
  const [sectorInput, setSectorInput] = useState('');
  const [regionInput, setRegionInput] = useState('');
  const [topicsInput, setTopicsInput] = useState('');
  const [assetRefsInput, setAssetRefsInput] = useState('');
  const [contentBlocksInput, setContentBlocksInput] = useState('');
  const [sourcesInput, setSourcesInput] = useState('[]');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewQualityScore, setReviewQualityScore] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [taxonomy, setTaxonomy] = useState({ practices: [], functions: [], cross_practice_topics: [] });

  useEffect(() => {
    fetchCategories();
    fetchTaxonomy();
    if (isEditMode) {
      fetchBlog();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(response.data.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/blogs/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blog = response.data.data;
      setFormData({
        title: blog.title || '',
        subtitle: blog.subtitle || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        category: blog.category || '',
        tags: blog.tags || [],
        content_type: blog.content_type || 'article',
        template_key: blog.template_key || 'editorial_article',
        visibility: blog.visibility || 'public',
        audience: blog.audience || [],
        sector: blog.sector || [],
        region: blog.region || [],
        topics: blog.topics || [],
        author_role: blog.author_role || '',
        editor_id: blog.editor_id || '',
        reviewer_id: blog.reviewer_id || '',
        scheduled_at: blog.scheduled_at || '',
        featured_rank: blog.featured_rank ?? '',
        content_blocks: blog.content_blocks ? JSON.stringify(blog.content_blocks, null, 2) : '',
        media_type: blog.media_type || 'image',
        media_url: blog.media_url || '',
        video_url: blog.video_url || '',
        video_duration_seconds: blog.video_duration_seconds ?? '',
        video_caption: blog.video_caption || '',
        external_url: blog.external_url || '',
        asset_refs: Array.isArray(blog.asset_refs) ? blog.asset_refs.join(', ') : (blog.asset_refs || ''),
        hero_headline: blog.hero_headline || '',
        hero_supporting_line: blog.hero_supporting_line || '',
        primary_cta_label: blog.primary_cta_label || '',
        primary_cta_url: blog.primary_cta_url || '',
        secondary_cta_label: blog.secondary_cta_label || '',
        secondary_cta_url: blog.secondary_cta_url || '',
        featured_image: blog.featured_image || '',
        featured_image_alt: blog.featured_image_alt || '',
        meta_title: blog.meta_title || '',
        meta_description: blog.meta_description || '',
        is_featured: blog.is_featured || false,
        status: blog.status || 'draft',
        editorial_status: blog.editorial_status || 'draft',
        publication_version: blog.publication_version || 1,
        approved_version: blog.approved_version ?? null,
        approved_by_name: blog.approved_by_name || '',
        approved_at: blog.approved_at || '',
        practice: blog.practice || '',
        vertical: blog.vertical || '',
        practice_id: blog.practice_id || '',
        practice_name: blog.practice_name || blog.practice || '',
        primary_vertical_id: blog.primary_vertical_id || '',
        vertical_ids: blog.vertical_ids || [],
        vertical_name: blog.vertical_name || blog.vertical || '',
        subcategory_ids: blog.subcategory_ids || [],
        function_ids: blog.function_ids || [],
        functional_role_ids: blog.functional_role_ids || [],
        cross_practice_topic_ids: blog.cross_practice_topic_ids || [],
        taxonomy_version: blog.taxonomy_version || '',
        evidence_notes: blog.evidence_notes || '',
        confidence_score: blog.confidence_score ?? ''
      });
      setAudienceInput((blog.audience || []).join(', '));
      setSectorInput((blog.sector || []).join(', '));
      setRegionInput((blog.region || []).join(', '));
      setTopicsInput((blog.topics || []).join(', '));
      setAssetRefsInput(Array.isArray(blog.asset_refs) ? blog.asset_refs.join(', ') : (blog.asset_refs || ''));
      setContentBlocksInput(blog.content_blocks ? JSON.stringify(blog.content_blocks, null, 2) : '');
      setSourcesInput(JSON.stringify(blog.sources || [], null, 2));
      fetchReviews();
    } catch (err) {
      alert(`Error loading blog: ${err.message}`);
      navigate('/admin/market-intelligence');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxonomy = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/market-intelligence/taxonomy`);
      setTaxonomy(response.data.data || { practices: [], functions: [], cross_practice_topics: [] });
    } catch (err) {
      console.error('Error fetching market intelligence taxonomy:', err);
    }
  };

  const fetchReviews = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/blogs/${id}/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setReviews(response.data.data || []);
    } catch (err) {
      console.error('Error loading editorial reviews:', err);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a prompt for AI generation');
      return;
    }

    try {
      setGenerating(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/blogs/generate`,
        {
          prompt: aiPrompt,
          ...aiOptions
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const generatedData = response.data.data;

      // Fill form with AI-generated content
      setFormData({
        ...formData,
        title: generatedData.title || '',
        subtitle: generatedData.subtitle || '',
        content: generatedData.content || '',
        excerpt: generatedData.excerpt || '',
        category: aiOptions.category,
        tags: generatedData.tags || [],
        featured_image: generatedData.featured_image || '',
        featured_image_alt: generatedData.featured_image_alt || '',
        meta_title: generatedData.meta_title || '',
        meta_description: generatedData.meta_description || '',
        hero_headline: generatedData.hero_headline || '',
        hero_supporting_line: generatedData.hero_supporting_line || ''
      });

      setShowAIModal(false);
      alert('Market intelligence draft generated successfully. Review and edit as needed.');
    } catch (err) {
      alert(`Error generating blog: ${err.response?.data?.details || err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (action = 'draft') => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      let parsedContentBlocks = [];
      let parsedSources = [];
      if (contentBlocksInput.trim()) {
        try {
          parsedContentBlocks = JSON.parse(contentBlocksInput);
        } catch (parseErr) {
          alert('Content blocks must be valid JSON.');
          return;
        }
      }
      try {
        parsedSources = JSON.parse(sourcesInput || '[]');
        if (!Array.isArray(parsedSources)) throw new Error('Sources must be an array');
      } catch (parseErr) {
        alert('Sources must be a valid JSON array.');
        return;
      }

      const dataToSave = {
        ...formData,
        audience: audienceInput.split(',').map((item) => item.trim()).filter(Boolean),
        sector: sectorInput.split(',').map((item) => item.trim()).filter(Boolean),
        region: regionInput.split(',').map((item) => item.trim()).filter(Boolean),
        topics: topicsInput.split(',').map((item) => item.trim()).filter(Boolean),
        asset_refs: assetRefsInput.split(',').map((item) => item.trim()).filter(Boolean),
        content_blocks: parsedContentBlocks,
        sources: parsedSources,
        confidence_score: formData.confidence_score === '' ? null : Number(formData.confidence_score),
        featured_rank: formData.featured_rank === '' ? null : Number(formData.featured_rank),
        video_duration_seconds: formData.video_duration_seconds === '' ? null : Number(formData.video_duration_seconds),
        status: formData.status
      };

      let targetId = id;
      if (isEditMode) {
        await axios.put(
          `${API_BASE_URL}/api/admin/blogs/${id}`,
          dataToSave,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Market intelligence article updated successfully.');
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/admin/blogs`,
          dataToSave,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        targetId = response.data.data?.id || response.data.id;
        alert('Market intelligence article created successfully.');
      }

      if (action === 'submit_review') {
        await axios.post(
          `${API_BASE_URL}/api/admin/blogs/${targetId}/submit-review`,
          {},
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        alert('Article submitted for human review.');
      }
      navigate('/admin/market-intelligence');
    } catch (err) {
      alert(`Error saving blog: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReviewDecision = async (decision) => {
    if ((decision === 'changes_requested' || decision === 'rejected') && !reviewNotes.trim()) {
      alert('Please add reviewer notes before requesting changes or rejecting.');
      return;
    }
    try {
      setReviewing(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/blogs/${id}/review`,
        {
          decision,
          notes: reviewNotes,
          quality_score: reviewQualityScore === '' ? null : Number(reviewQualityScore)
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      await fetchBlog();
      setReviewNotes('');
      alert(`Review decision recorded: ${decision.replaceAll('_', ' ')}`);
    } catch (err) {
      alert(err.response?.data?.error || `Review failed: ${err.message}`);
    } finally {
      setReviewing(false);
    }
  };

  const handlePublishApproved = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/admin/blogs/${id}/publish`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Approved article published.');
      navigate('/admin/market-intelligence');
    } catch (err) {
      alert(err.response?.data?.error || `Publishing failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const selectedPractice = (taxonomy.practices || []).find(
    (practice) => practice.id === formData.practice_id
  );
  const availableVerticals = selectedPractice?.verticals || [];
  const selectedVerticals = availableVerticals.filter(
    (vertical) => (formData.vertical_ids || []).includes(vertical.id)
  );
  const availableSubcategories = selectedVerticals.flatMap(
    (vertical) => vertical.subcategories || []
  );
  const availableFunctionalRoles = (taxonomy.functions || [])
    .filter((group) => (formData.function_ids || []).includes(group.id))
    .flatMap((group) => group.roles || []);

  const toggleTaxonomyValue = (field, value) => {
    const current = formData[field] || [];
    if (field === 'vertical_ids' && value === formData.primary_vertical_id && current.includes(value)) {
      return;
    }
    setFormData({
      ...formData,
      [field]: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    });
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p>Loading blog editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit Market Intelligence Article' : 'Create Market Intelligence Article'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Review evidence, taxonomy and editorial content' : 'Draft an evidence-led article for human review'}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAIModal(true)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium"
            >
              ✨ Generate with AI
            </button>
            <button
              onClick={() => navigate('/admin/market-intelligence')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSave('submit_review')}
              disabled={saving}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {saving ? 'Submitting...' : 'Submit for Human Review'}
            </button>
          </div>
        </div>
      </div>

      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Generate Intelligence Draft with AI</h2>

            <div className="space-y-4">
              {/* Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What should the blog be about?
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., Write about the future of AI in recruitment..."
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={aiOptions.category}
                  onChange={(e) => setAiOptions({...aiOptions, category: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tone
                </label>
                <select
                  value={aiOptions.tone}
                  onChange={(e) => setAiOptions({...aiOptions, tone: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="enthusiastic">Enthusiastic</option>
                  <option value="formal">Formal</option>
                </select>
              </div>

              {/* Length */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Length
                </label>
                <select
                  value={aiOptions.length}
                  onChange={(e) => setAiOptions({...aiOptions, length: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="short">Short (500-800 words)</option>
                  <option value="medium">Medium (800-1200 words)</option>
                  <option value="long">Long (1200-2000 words)</option>
                </select>
              </div>

              {/* Image Style */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Image Style
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="photorealistic"
                      checked={aiOptions.imageStyle === 'photorealistic'}
                      onChange={(e) => setAiOptions({...aiOptions, imageStyle: e.target.value})}
                      className="mr-2"
                    />
                    <span>Photorealistic</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="illustrative"
                      checked={aiOptions.imageStyle === 'illustrative'}
                      onChange={(e) => setAiOptions({...aiOptions, imageStyle: e.target.value})}
                      className="mr-2"
                    />
                    <span>Illustrative</span>
                  </label>
                </div>
              </div>

              {/* Generate Images */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={aiOptions.include_images}
                    onChange={(e) => setAiOptions({...aiOptions, include_images: e.target.checked})}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Generate featured image with DALL-E 3
                  </span>
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAIModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateWithAI}
                disabled={generating}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
              >
                {generating ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating with AI...
                  </span>
                ) : (
                  'Generate Intelligence Draft'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="space-y-6">
          <section className="rounded-xl border border-orange-200 bg-orange-50 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-orange-700">Editorial governance</p>
                <h2 className="mt-1 text-xl font-bold text-gray-900">Evidence and human approval</h2>
                <p className="mt-1 text-sm text-gray-600">AI may prepare the draft. A human reviewer must approve the current version before publication.</p>
              </div>
              <div className="text-right text-sm">
                <span className="rounded-full bg-white px-3 py-1 font-semibold text-gray-800">
                  {(formData.editorial_status || 'draft').replaceAll('_', ' ')}
                </span>
                <p className="mt-2 text-gray-600">Version {formData.publication_version || 1}</p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Practice *</label>
                <select
                  value={formData.practice_id || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    practice_id: e.target.value,
                    primary_vertical_id: '',
                    vertical_ids: [],
                    subcategory_ids: []
                  })}
                  className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select practice...</option>
                  {(taxonomy.practices || []).map((practice) => (
                    <option key={practice.id} value={practice.id}>{practice.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Primary vertical *</label>
                <select
                  value={formData.primary_vertical_id || ''}
                  disabled={!formData.practice_id}
                  onChange={(e) => {
                    const verticalId = e.target.value;
                    setFormData({
                      ...formData,
                      primary_vertical_id: verticalId,
                      vertical_ids: verticalId
                        ? Array.from(new Set([verticalId, ...(formData.vertical_ids || [])]))
                        : []
                    });
                  }}
                  className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select vertical...</option>
                  {availableVerticals.map((vertical) => (
                    <option key={vertical.id} value={vertical.id}>{vertical.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Confidence score (0-100)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.confidence_score ?? ''}
                  onChange={(e) => setFormData({...formData, confidence_score: e.target.value})}
                  className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Evidence notes</label>
                <input
                  value={formData.evidence_notes || ''}
                  onChange={(e) => setFormData({...formData, evidence_notes: e.target.value})}
                  placeholder="What was verified, inferred, or needs review?"
                  className="w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            {availableVerticals.length > 0 && (
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-gray-700">Vertical coverage</label>
                <div className="flex flex-wrap gap-2">
                  {availableVerticals.map((vertical) => (
                    <button
                      type="button"
                      key={vertical.id}
                      onClick={() => toggleTaxonomyValue('vertical_ids', vertical.id)}
                      className={`rounded-full border px-3 py-1.5 text-sm ${
                        (formData.vertical_ids || []).includes(vertical.id)
                          ? 'border-orange-500 bg-orange-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {vertical.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {availableSubcategories.length > 0 && (
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-gray-700">Sub-categories and topics</label>
                <div className="max-h-44 overflow-y-auto rounded-lg border bg-white p-3">
                  <div className="flex flex-wrap gap-2">
                    {availableSubcategories.map((topic) => (
                      <button
                        type="button"
                        key={topic.id}
                        onClick={() => toggleTaxonomyValue('subcategory_ids', topic.id)}
                        className={`rounded-full border px-3 py-1 text-xs ${
                          (formData.subcategory_ids || []).includes(topic.id)
                            ? 'border-slate-900 bg-slate-900 text-white'
                            : 'border-gray-300 text-gray-700'
                        }`}
                      >
                        {topic.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Functional coverage</label>
                <div className="flex flex-wrap gap-2">
                  {(taxonomy.functions || []).map((group) => (
                    <button
                      type="button"
                      key={group.id}
                      onClick={() => toggleTaxonomyValue('function_ids', group.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs ${
                        (formData.function_ids || []).includes(group.id)
                          ? 'border-blue-700 bg-blue-700 text-white'
                          : 'border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Cross-practice technology topics</label>
                <div className="flex flex-wrap gap-2">
                  {(taxonomy.cross_practice_topics || []).map((topic) => (
                    <button
                      type="button"
                      key={topic.id}
                      onClick={() => toggleTaxonomyValue('cross_practice_topic_ids', topic.id)}
                      className={`rounded-full border px-3 py-1.5 text-xs ${
                        (formData.cross_practice_topic_ids || []).includes(topic.id)
                          ? 'border-teal-700 bg-teal-700 text-white'
                          : 'border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {topic.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {availableFunctionalRoles.length > 0 && (
              <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-gray-700">Functional roles</label>
                <div className="flex flex-wrap gap-2">
                  {availableFunctionalRoles.map((role) => (
                    <button
                      type="button"
                      key={role.id}
                      onClick={() => toggleTaxonomyValue('functional_role_ids', role.id)}
                      className={`rounded-full border px-3 py-1 text-xs ${
                        (formData.functional_role_ids || []).includes(role.id)
                          ? 'border-indigo-700 bg-indigo-700 text-white'
                          : 'border-gray-300 bg-white text-gray-700'
                      }`}
                    >
                      {role.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Sources * (JSON array)</label>
              <textarea
                rows={6}
                value={sourcesInput}
                onChange={(e) => setSourcesInput(e.target.value)}
                placeholder='[{"title":"Source title","url":"https://...","publisher":"Publisher","published_at":"2026-07-30"}]'
                className="w-full rounded-lg border px-4 py-3 font-mono text-sm focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </section>

          {isEditMode && formData.editorial_status === 'awaiting_human_review' && (
            <section className="rounded-xl border border-blue-200 bg-blue-50 p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Human review inbox</p>
              <h2 className="mt-1 text-xl font-bold text-gray-900">Review the current publication version</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <textarea
                  rows={3}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Reviewer notes, corrections, or approval rationale"
                  className="md:col-span-2 w-full rounded-lg border px-4 py-3"
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={reviewQualityScore}
                  onChange={(e) => setReviewQualityScore(e.target.value)}
                  placeholder="Quality score (0-100)"
                  className="h-11 rounded-lg border px-4 py-2"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button disabled={reviewing} onClick={() => handleReviewDecision('approved')} className="rounded-lg bg-green-600 px-4 py-2 font-semibold text-white">Approve</button>
                <button disabled={reviewing} onClick={() => handleReviewDecision('changes_requested')} className="rounded-lg bg-amber-500 px-4 py-2 font-semibold text-white">Request changes</button>
                <button disabled={reviewing} onClick={() => handleReviewDecision('rejected')} className="rounded-lg bg-red-600 px-4 py-2 font-semibold text-white">Reject</button>
              </div>
            </section>
          )}

          {isEditMode && formData.editorial_status === 'human_approved' && (
            <section className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-green-200 bg-green-50 p-5">
              <div>
                <h2 className="font-bold text-green-900">Human approved</h2>
                <p className="text-sm text-green-800">Approved by {formData.approved_by_name || 'reviewer'} for version {formData.approved_version}.</p>
              </div>
              <button onClick={handlePublishApproved} disabled={saving} className="rounded-lg bg-green-700 px-5 py-2 font-semibold text-white">
                Publish Approved Article
              </button>
            </section>
          )}

          {reviews.length > 0 && (
            <section className="rounded-xl border bg-gray-50 p-5">
              <h2 className="font-bold text-gray-900">Review history</h2>
              <div className="mt-3 space-y-2">
                {reviews.slice(0, 5).map((review) => (
                  <div key={review.id || `${review.created_at}-${review.decision}`} className="rounded-lg bg-white p-3 text-sm">
                    <span className="font-semibold">{(review.decision || '').replaceAll('_', ' ')}</span>
                    <span className="text-gray-500"> · version {review.publication_version} · {review.reviewer_name || review.reviewer_id}</span>
                    {review.notes && <p className="mt-1 text-gray-700">{review.notes}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Enter market intelligence headline..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subtitle
            </label>
            <input
              type="text"
              value={formData.subtitle}
              onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
              placeholder="Optional supporting line for the hero..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Category and Featured */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Options
              </label>
              <label className="flex items-center mt-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm">Mark as Featured</span>
              </label>
            </div>
          </div>

          {/* Market Intelligence CMS */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Market Intelligence CMS</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                <select
                  value={formData.content_type}
                  onChange={(e) => setFormData({...formData, content_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="article">Article</option>
                  <option value="briefing">Briefing</option>
                  <option value="report">Report</option>
                  <option value="video">Video</option>
                  <option value="analysis">Analysis</option>
                  <option value="guide">Guide</option>
                  <option value="methodology">Methodology</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Template Key</label>
                <input
                  type="text"
                  value={formData.template_key}
                  onChange={(e) => setFormData({...formData, template_key: e.target.value})}
                  placeholder="editorial_article / briefing / market_map / video_brief"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData({...formData, visibility: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="public">Public</option>
                  <option value="subscriber_only">Subscriber only</option>
                  <option value="internal">Internal</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Author Role</label>
                <input
                  type="text"
                  value={formData.author_role}
                  onChange={(e) => setFormData({...formData, author_role: e.target.value})}
                  placeholder="Editor / Analyst / Publisher"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Editor ID</label>
                <input
                  type="text"
                  value={formData.editor_id}
                  onChange={(e) => setFormData({...formData, editor_id: e.target.value})}
                  placeholder="Optional internal ID"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reviewer ID</label>
                <input
                  type="text"
                  value={formData.reviewer_id}
                  onChange={(e) => setFormData({...formData, reviewer_id: e.target.value})}
                  placeholder="Optional internal ID"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Scheduled At</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Featured Rank</label>
                <input
                  type="number"
                  min="0"
                  value={formData.featured_rank}
                  onChange={(e) => setFormData({...formData, featured_rank: e.target.value})}
                  placeholder="0"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
                <input
                  type="text"
                  value={audienceInput}
                  onChange={(e) => setAudienceInput(e.target.value)}
                  placeholder="investors, subscribers, clients"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sector</label>
                <input
                  type="text"
                  value={sectorInput}
                  onChange={(e) => setSectorInput(e.target.value)}
                  placeholder="commodities, metals, finance"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                <input
                  type="text"
                  value={regionInput}
                  onChange={(e) => setRegionInput(e.target.value)}
                  placeholder="APAC, Singapore, EMEA"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Topics</label>
                <input
                  type="text"
                  value={topicsInput}
                  onChange={(e) => setTopicsInput(e.target.value)}
                  placeholder="market map, hiring, talent intelligence"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Headline</label>
                <input
                  type="text"
                  value={formData.hero_headline}
                  onChange={(e) => setFormData({...formData, hero_headline: e.target.value})}
                  placeholder="Strong front-page headline..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Supporting Line</label>
                <input
                  type="text"
                  value={formData.hero_supporting_line}
                  onChange={(e) => setFormData({...formData, hero_supporting_line: e.target.value})}
                  placeholder="Supportive subheadline..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Label</label>
                <input
                  type="text"
                  value={formData.primary_cta_label}
                  onChange={(e) => setFormData({...formData, primary_cta_label: e.target.value})}
                  placeholder="Read briefing"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA URL</label>
                <input
                  type="url"
                  value={formData.primary_cta_url}
                  onChange={(e) => setFormData({...formData, primary_cta_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Label</label>
                <input
                  type="text"
                  value={formData.secondary_cta_label}
                  onChange={(e) => setFormData({...formData, secondary_cta_label: e.target.value})}
                  placeholder="Subscribe"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA URL</label>
                <input
                  type="url"
                  value={formData.secondary_cta_url}
                  onChange={(e) => setFormData({...formData, secondary_cta_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">External URL</label>
                <input
                  type="url"
                  value={formData.external_url}
                  onChange={(e) => setFormData({...formData, external_url: e.target.value})}
                  placeholder="Source link or partner reference..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset References</label>
                <input
                  type="text"
                  value={assetRefsInput}
                  onChange={(e) => setAssetRefsInput(e.target.value)}
                  placeholder="logo.png, chart.svg, video-01.mp4"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Media Type</label>
                <select
                  value={formData.media_type}
                  onChange={(e) => setFormData({...formData, media_type: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="none">None</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Media URL</label>
                <input
                  type="url"
                  value={formData.media_url}
                  onChange={(e) => setFormData({...formData, media_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video URL</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video Duration (seconds)</label>
                <input
                  type="number"
                  min="90"
                  max="120"
                  value={formData.video_duration_seconds}
                  onChange={(e) => setFormData({...formData, video_duration_seconds: e.target.value})}
                  placeholder="90 - 120"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">Video clips must be between 90 and 120 seconds.</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Video Caption</label>
                <input
                  type="text"
                  value={formData.video_caption}
                  onChange={(e) => setFormData({...formData, video_caption: e.target.value})}
                  placeholder="Short caption for video preview..."
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Blocks JSON</label>
                <textarea
                  value={contentBlocksInput}
                  onChange={(e) => setContentBlocksInput(e.target.value)}
                  rows={6}
                  placeholder='[{"type":"paragraph","text":"..."}]'
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Optional structured blocks for the public hub pages.</p>
              </div>
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Excerpt
            </label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
              placeholder="Brief summary of the intelligence article..."
              rows={2}
              maxLength={150}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.excerpt.length}/150 characters</p>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={(value) => setFormData({...formData, content: value})}
              modules={quillModules}
              className="bg-white"
              style={{ height: '400px', marginBottom: '50px' }}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag..."
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                + Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-blue-500 hover:text-blue-700"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Featured Image URL
            </label>
            <input
              type="url"
              value={formData.featured_image}
              onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
            {formData.featured_image && (
              <img
                src={formData.featured_image}
                alt="Preview"
                className="mt-2 h-48 object-cover rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </div>

          {/* Image Alt Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Alt Text
            </label>
            <input
              type="text"
              value={formData.featured_image_alt}
              onChange={(e) => setFormData({...formData, featured_image_alt: e.target.value})}
              placeholder="Description of the image..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* SEO Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Settings</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title
                </label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                  placeholder="SEO optimized title..."
                  maxLength={60}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.meta_title.length}/60 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                  placeholder="SEO meta description..."
                  rows={2}
                  maxLength={160}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.meta_description.length}/160 characters</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              onClick={() => navigate('/admin/market-intelligence')}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              Save Draft
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;

